import { LocationState } from 'features/Login'

type GetRedirectUrlParams = {
  currentUrl: string
  newSystemBaseUrl: string
  defaultRedirectPath?: string
  locationState?: LocationState
}

const OLD_SYSTEM_REDIRECT_PARAM = 'redirectTo'
const OLD_SYSTEM_LOGIN_AS_PATH = '/loginas'
const OLD_SYSTEM_LOGIN_PATH = '/login'
const NEW_SYSTEM_LOGIN_AS_PATH = '/login-as'
const NEW_SYSTEM_REDIRECT_PARAM = 'redirect'

/**
 * Generates a redirect URL for migrating users to new ship
 *
 * @param currentUrl - The current URL the user is on (e.g., window.location.href)
 * @param newSystemBaseUrl - The base URL of the new ship
 * @param defaultRedirectPath - Optional default path to redirect to (defaults to '/')
 * @returns The complete URL to redirect the user to
 */
export const getRedirectUrl = ({
  currentUrl,
  newSystemBaseUrl,
  defaultRedirectPath = '/',
  locationState,
}: GetRedirectUrlParams): string => {
  try {
    const current = new URL(currentUrl)

    // Extract the existing redirect path from query params or history if it exists
    // This is usually when a TMS user uses LoginAs to impersonate a user
    const existingNextPathName = locationState?.nextPathname
    const existingRedirect = current.searchParams.get(OLD_SYSTEM_REDIRECT_PARAM) || existingNextPathName

    // Determine the final redirect path
    let redirectPath: string
    if (existingRedirect) {
      // If there's an existing redirect, use that as the base path
      const redirectUrl = new URL(existingRedirect, current.origin)
      // Preserve any query parameters from the existing redirect
      // This is more of an edge case, but it's possible that the redirect also has search params
      redirectPath = `${redirectUrl.pathname}${redirectUrl.search}`
    } else if (
      current.pathname.includes(OLD_SYSTEM_LOGIN_AS_PATH) ||
      current.pathname.includes(OLD_SYSTEM_LOGIN_PATH)
    ) {
      // If current page is loginas, but has no redirect param
      // redirect to default path on the new ship
      redirectPath = defaultRedirectPath
    } else {
      // Use current path with its query parameters
      redirectPath = `${current.pathname}${current.search}`
    }

    // Construct the final URL for the new ship
    const newSystemUrl = new URL(NEW_SYSTEM_LOGIN_AS_PATH, newSystemBaseUrl)
    newSystemUrl.searchParams.set(NEW_SYSTEM_REDIRECT_PARAM, redirectPath)

    return newSystemUrl.toString()
  } catch (error) {
    // Generate fallback URL with error information
    // our assumption here is that newSystemBaseUrl is always a valid URL
    const newSystemUrl = new URL(NEW_SYSTEM_LOGIN_AS_PATH, newSystemBaseUrl)
    const errorParams = new URLSearchParams()
    let errorMessage: string

    if (error instanceof TypeError) {
      errorMessage = 'Invalid URL format'
    } else if (error instanceof Error) {
      errorMessage = error.message
    } else {
      errorMessage = 'Unknown error occurred'
    }

    errorParams.set('error', `Failed to process redirect: ${errorMessage}`)
    newSystemUrl.searchParams.set(NEW_SYSTEM_REDIRECT_PARAM, `${defaultRedirectPath}?${errorParams.toString()}`)
    return newSystemUrl.toString()
  }
}
